import { useEffect, useMemo, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, Image as ImageIcon, MapPin, User } from "lucide-react";
import { toast } from "sonner";

const PRIO_META = {
  low: "bg-neutral-100 text-neutral-700 border-neutral-200",
  medium: "bg-sky-100 text-sky-800 border-sky-200",
  high: "bg-amber-100 text-amber-800 border-amber-200",
  critical: "bg-red-100 text-red-800 border-red-200",
};
const STATUS_META = {
  open: "bg-amber-100 text-amber-800 border-amber-200",
  in_progress: "bg-sky-100 text-sky-800 border-sky-200",
  resolved: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

function AuthedImage({ path }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url; let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/files/${path}`, { responseType: "blob" });
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setSrc(url);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!src) return <div className="w-full aspect-video bg-neutral-100 animate-pulse rounded-md" />;
  return <img src={src} alt="snag" className="w-full aspect-video object-cover rounded-md border border-neutral-200" />;
}

const emptySnag = {
  project_id: "", title: "", description: "", location: "",
  priority: "medium", status: "open", assigned_to: "",
  photo_path: null, photo_url: null,
};

export default function SnagsPage() {
  const { t, lang } = useLang();
  const [snags, setSnags] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptySnag);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [{ data: s }, { data: p }] = await Promise.all([
        api.get(`/snags?lang=${lang}`),
        api.get(`/projects?lang=${lang}`),
      ]);
      setSnags(s); setProjects(p);
      if (!form.project_id && p[0]) setForm((f) => ({ ...f, project_id: p[0].id }));
      if (!selectedId && s[0]) setSelectedId(s[0].id);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [lang]);

  const filtered = useMemo(
    () => snags.filter((s) => projectFilter === "all" || s.project_id === projectFilter),
    [snags, projectFilter]
  );
  const selected = filtered.find((s) => s.id === selectedId) || filtered[0] || null;

  const stats = useMemo(() => {
    const total = snags.length;
    const open = snags.filter((s) => s.status === "open").length;
    const crit = snags.filter((s) => s.priority === "critical").length;
    return { total, open, crit };
  }, [snags]);

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, photo_path: data.path, photo_url: data.url }));
      toast.success(t("photo_uploaded"));
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUploading(false); }
  };

  const create = async () => {
    if (!form.project_id || !form.title) { toast.error(t("fill_title_project")); return; }
    try {
      const { data } = await api.post("/snags", form);
      setDialogOpen(false); setForm(emptySnag);
      await load(); setSelectedId(data.id);
      toast.success(t("snag_created"));
      if (data.priority === "critical") window.dispatchEvent(new Event("obra:notif-refresh"));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async (id) => {
    if (!confirm(t("delete_snag_confirm"))) return;
    try { await api.delete(`/snags/${id}`); await load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const updateSelected = async (patch) => {
    if (!selected) return;
    try {
      const { data } = await api.patch(`/snags/${selected.id}`, patch);
      setSnags((prev) => prev.map((s) => (s.id === selected.id ? data : s)));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="snags-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-eyebrow">{t("never_forget_issue")}</p>
          <h1 className="font-[Fraunces] text-4xl font-bold tracking-tight text-neutral-950 mt-1">{t("snags")}</h1>
        </div>
        <div className="flex items-center gap-3">
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-56" data-testid="snags-project-filter">
              <SelectValue placeholder={t("filter_project")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#B8933A] hover:bg-[#8C6D22]" data-testid="new-snag-button">
                <Plus className="w-4 h-4 mr-1" />{t("new_snag")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-[Fraunces]">{t("new_snag")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label className="label-eyebrow">{t("snag_title")}</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="snag-title-input" />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label className="label-eyebrow">{t("projects")}</Label>
                  <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                    <SelectTrigger data-testid="snag-project-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-eyebrow">{t("priority")}</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="snag-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">{t("low")}</SelectItem>
                      <SelectItem value="medium">{t("medium")}</SelectItem>
                      <SelectItem value="high">{t("high")}</SelectItem>
                      <SelectItem value="critical">{t("critical")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-eyebrow">{t("location")}</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label className="label-eyebrow">{t("assigned_to")}</Label>
                  <Input value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label className="label-eyebrow">{t("description")}</Label>
                  <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="snag-desc-input" />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label className="label-eyebrow">{t("upload_photo")}</Label>
                  <div
                    onClick={() => fileRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) uploadFile(f); }}
                    className="border-2 border-dashed border-neutral-300 hover:border-[#8C6D22] rounded-md p-6 text-center cursor-pointer transition-colors duration-200"
                    data-testid="snag-photo-drop">
                    {uploading ? <p className="text-sm text-neutral-500">{t("uploading")}</p> :
                      form.photo_path ?
                        <p className="text-sm text-emerald-700">{t("photo_attached")}</p> :
                        <p className="text-sm text-neutral-500 flex items-center justify-center gap-2"><ImageIcon className="w-4 h-4" />{t("drop_photo")}</p>}
                    <input ref={fileRef} type="file" accept="image/*" hidden
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); }} />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("cancel")}</Button>
                <Button className="bg-[#B8933A] hover:bg-[#8C6D22]" onClick={create} data-testid="save-snag-button">{t("save")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="surface-card p-5"><p className="label-eyebrow">{t("total_snags")}</p><p className="font-[Fraunces] text-3xl font-bold mt-1">{stats.total}</p></div>
        <div className="surface-card p-5"><p className="label-eyebrow">{t("open_snags")}</p><p className="font-[Fraunces] text-3xl font-bold mt-1 text-amber-700">{stats.open}</p></div>
        <div className="surface-card p-5"><p className="label-eyebrow">{t("critical_snags")}</p><p className="font-[Fraunces] text-3xl font-bold mt-1 text-red-700">{stats.crit}</p></div>
      </div>

      {/* Split view */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-2" data-testid="snag-list">
          {filtered.length === 0 && (
            <div className="surface-card p-8 text-center text-sm text-neutral-500">{t("no_snags")}</div>
          )}
          {filtered.map((s) => (
            <button key={s.id}
              onClick={() => setSelectedId(s.id)}
              data-testid={`snag-item-${s.id}`}
              className={`w-full text-left surface-card p-4 transition-colors duration-200 ${selected?.id === s.id ? "border-[#B8933A] bg-[#FAF6EA]" : "hover:border-neutral-400"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[11px] text-neutral-500 truncate">{projectName(s.project_id)}</p>
                  <p className="font-medium text-neutral-950 mt-0.5 truncate">{s.title}</p>
                </div>
                <Badge variant="outline" className={`border ${PRIO_META[s.priority]}`}>{t(s.priority)}</Badge>
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs">
                <Badge variant="outline" className={`border ${STATUS_META[s.status]}`}>{t(s.status)}</Badge>
                {s.location && <span className="text-neutral-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{s.location}</span>}
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-8">
          {!selected ? (
            <div className="surface-card p-12 text-center text-neutral-500">{t("select_snag_prompt")}</div>
          ) : (
            <div className="surface-card p-6 space-y-6" data-testid="snag-detail-panel">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="label-eyebrow">{projectName(selected.project_id)}</p>
                  <h2 className="font-[Fraunces] font-bold text-2xl text-neutral-950 mt-1">{selected.title}</h2>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    <Badge variant="outline" className={`border ${PRIO_META[selected.priority]}`}>{t(selected.priority)}</Badge>
                    <Badge variant="outline" className={`border ${STATUS_META[selected.status]}`}>{t(selected.status)}</Badge>
                    {selected.location && <span className="text-neutral-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{selected.location}</span>}
                    {selected.assigned_to && <span className="text-neutral-500 flex items-center gap-1"><User className="w-3 h-3" />{selected.assigned_to}</span>}
                  </div>
                </div>
                <button onClick={() => remove(selected.id)} className="text-neutral-400 hover:text-red-600 transition-colors duration-200" data-testid="delete-selected-snag">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {selected.photo_path && (<AuthedImage path={selected.photo_path} />)}

              {selected.description && (
                <div>
                  <p className="label-eyebrow mb-2">{t("description")}</p>
                  <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap">{selected.description}</p>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3">
                <Label className="label-eyebrow">{t("status")}</Label>
                <Select value={selected.status} onValueChange={(v) => updateSelected({ status: v })}>
                  <SelectTrigger className="w-40" data-testid="update-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">{t("open")}</SelectItem>
                    <SelectItem value="in_progress">{t("in_progress")}</SelectItem>
                    <SelectItem value="resolved">{t("resolved")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
